import { lazy, Suspense, useContext, useEffect, useRef, useState } from "react";
import "./ConfigureShelf.css";
import BinCapacityError from "./BinCapacityErrorDialog/BinCapacityError"
//Lodash
import { round } from "lodash";

//Store
import { Store } from "../../store";

//Router
import { useLocation } from "react-router-dom";

//API
import axios from "../../service/axios";

//Helpers
import { CloseCircleFilled, DownOutlined, InfoCircleFilled } from "@ant-design/icons";
import { Button, Dropdown, Spin } from "antd";
import { toast } from "react-toastify";
import AppModal from "../../shared/components/app-modal/AppModal";
import ToolTip from "../../shared/components/ToolTip/ToolTip";
import { UNIT_TYPES } from "../../utils/constant";
import { getDuplicateCodes, pixelsToUnits, transformElement, unitsToPixels } from "../../utils/helpers";
import { createSubAreas } from "../LayoutEditor/drawUtils";
import RackFrontView from "../RackFrontView/Index";
import GenerateSubArea from "./GenerateSubAreaForm/GenerateSubArea";
import SubAreaBoard from "./SubAreaBoard/SubAreaBoard";

const MIN_WALL_HEIGHT = 200;

// lazy load components
const SegmentedToggle = lazy(() => import("../../shared/components/SegmentedToggle/SegmentedToggle"));

const ConfigureShelf = ({ handleShelfToggeler, shelf, updateSelectedShelf }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const layoutId = searchParams.get("layoutId");
  const parentRef = useRef(null);
  const childRef = useRef(null);
  const { state, dispatch } = useContext(Store);
  const [rackDirection, setRackDirection] = useState({});
  const [loading, setLoading] = useState(false);
  const [dialogData, setDialogData] = useState(null);
  const [saveRequested, setSaveRequested] = useState(false);
  const [saveEvent, setSaveEvent] = useState(null);
  const [errorPopup, setErrorPopup] = useState({ isOpen: false, message: "" });

  const [formData, setFormData] = useState({
    rackName: null,
    rackCode: null,
    rackX: null,
    rackY: null,
    rackWidth: null,
    rackHeight: null,
    wallHeight: null,
    unitType: shelf?.unitType || UNIT_TYPES.inch,
    maxCapacity: shelf?.maxCapacity || null,
  });

  const componentToView = (component_name) => {
    const components = {
      SUB_AREAS: <GenerateSubArea ref={childRef} selectedElement={shelf} formData={formData} />,
    };
    return components[component_name];
  };

  const [subAreas, setSubAreas] = useState(shelf?.subAreas || []);
  const onGenerateSubAreas = () => {
    try {
      const { rows, columns, maxCapacity } = childRef?.current?.getFormData();

      const updatedShelf = { ...shelf, code: formData.rackCode, name: formData.rackName };
      const subAreas = createSubAreas({
        element: updatedShelf,
        rows,
        columns,
        maxCapacity,
      });
      setSubAreas(subAreas);
    } catch (error) {
      console.log("error =>", error);
    } finally {
      setDialogData(null);
    }
  };

  const onCloseDialogHandler = () => {
    setDialogData(null);
  };

  const onConfirmDialogHandler = () => {
    dialogData?.action();
    setDialogData(null);
    // setGenerateBinsToggler(true);
  };

  const handeChange = (e) => {
    const { name, value, id } = e.target;
    setFormData({ ...formData, [id]: value });

    const rackWidth = shelf.x2 - shelf.x1;
    const rackHeight = shelf.y2 - shelf.y1;

    const lenp = rackWidth > rackHeight ? formData.rackHeight : formData.rackWidth;
    const widp = rackWidth > rackHeight ? formData.rackWidth : formData.rackHeight;

    const updatedElementData = {
      ...shelf,
      name: name === "rackName" ? value : formData.rackName,
      code: name === "rackCode" ? value : formData.rackCode,
      positionX: name === "rackX" ? value : formData.rackX,
      positionY: name === "rackY" ? value : formData.rackY,
      unitType: formData.unitType,
      hasElementChanges: true,
    };

    dispatch({ type: "UPDATE_ELEMENT", payload: updatedElementData });
  };

  const handleCancel = (e) => {
    e.preventDefault();
    if (shelf?.objectType === "area") {
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "UPDATE_ELEMENT", payload: { ...shelf, hasElementChanges: true } },
          { type: "RE_SET_ELEMENTS", payload: shelf?.subAreas || [] },
        ],
      });
      setSubAreas([]);
    }

    handleShelfToggeler();
  };

  useEffect(() => {
    let { name, code, x1, x2, y1, y2, rackView, objectType } = shelf;
    x1 = round(x1, 2);
    x2 = round(x2, 2);
    y1 = round(y1, 2);
    y2 = round(y2, 2);

    const rackWidth = x2 - x1;
    const rackHeight = y2 - y1;

    const calculatedRackWidth = rackWidth > rackHeight ? rackWidth : rackHeight;
    const calculatedRackHeight = rackWidth > rackHeight ? rackHeight : rackWidth;

    const data = {
      ...formData,
      rackName: name,
      rackCode: code,
      rackX: pixelsToUnits(x1, formData.unitType),
      rackY: pixelsToUnits(y1, formData.unitType),
      rackWidth: pixelsToUnits(calculatedRackWidth, formData.unitType),
      rackHeight: pixelsToUnits(calculatedRackHeight, formData.unitType),
    };
    if (objectType !== "rack") {
      data.rackWidth = pixelsToUnits(rackWidth, formData.unitType);
      data.rackHeight = pixelsToUnits(rackHeight, formData.unitType);
    }
    if (objectType === "wall") {
      data.wallHeight = shelf?.height ? pixelsToUnits(shelf?.height, formData.unitType) : null;
    }

    //Round off the values to 2 decimal places
    data.rackX = round(data.rackX, 2);
    data.rackY = round(data.rackY, 2);
    data.rackWidth = round(data.rackWidth, 2);
    data.rackHeight = round(data.rackHeight, 2);

    setFormData({ ...data });
    setRackDirection(rackView);

    //Dependency array - levels, columns, shelf, bins
  }, [shelf]);

  const labelGenerator = (labelName) => {
    const includeFields = ["Name", "Code"];
    // Determine base name and capitalize first letter
    const rawName = shelf?.objectType || "shelf";
    const name = rawName.charAt(0).toUpperCase() + rawName.slice(1);

    //Is rack show height as length and width as depth
    const isRack = shelf?.objectType === "rack";
    let label = "";
    if (isRack && labelName === "Width") {
      label = "Length";
    } else if (isRack && labelName === "Height") {
      label = "Depth";
    } else {
      label = labelName;
    }

    return label && !includeFields.includes(label) ? `${name} ${label} (${formData.unitType})` : `${name} ${label || ""}`;
  };

  const setUnitType = (unit) => {
    // if unit type is changing, update with new config
    if (formData.unitType !== unit) {
      // get the pixels version of current unit type

      let { rackX, rackY, rackWidth, rackHeight, unitType: currentUnitType, wallHeight } = formData;
      rackX = unitsToPixels(rackX, currentUnitType);
      rackY = unitsToPixels(rackY, currentUnitType);
      rackWidth = unitsToPixels(rackWidth, currentUnitType);
      rackHeight = unitsToPixels(rackHeight, currentUnitType);
      wallHeight = unitsToPixels(wallHeight, currentUnitType);

      rackX = round(rackX, 2);
      rackY = round(rackY, 2);
      rackWidth = round(rackWidth, 2);
      rackHeight = round(rackHeight, 2);
      // convert the pixels into new units

      rackX = round(pixelsToUnits(rackX, unit), 2);
      rackY = round(pixelsToUnits(rackY, unit), 2);
      rackWidth = round(pixelsToUnits(rackWidth, unit), 2);
      rackHeight = round(pixelsToUnits(rackHeight, unit), 2);
      wallHeight = round(pixelsToUnits(wallHeight, unit), 2);

      const newFormData = {
        ...formData,
        unitType: unit,
        rackX,
        rackY,
        rackWidth,
        rackHeight,
        wallHeight,
      };
      setFormData(newFormData);

      const updatedRack = {
        ...shelf,
        unitType: unit,
        hasElementChanges: true,
      };

      dispatch({ type: "UPDATE_ELEMENT", payload: updatedRack });
    }
  };

  const messages = (id) => {
    if (shelf.objectType === "rack") {
      return `Rack ${id ? "updated" : "created"} successfully`;
    } else {
      return `${shelf.objectType} ${id ? "updated" : "created"} successfully`;
    }
  };

  const handleSaveRack = async (e) => {
    setLoading(true);
    e.preventDefault();
    const { name, code, width, color, type, facingDirection, x, y, x1, y1, x2, y2, objectType, height, id, hasElementChanges, zIndex, unitType, rotation } =
      shelf;

    let payload = {};

    const rackWidth = x2 - x1;
    const rackHeight = y2 - y1;

    if (objectType === "rack") {
      // TODO
      // a method to see if the bins changed or not.
      // if it is not changed no need to update the bins, and no need to add bins in the payload

      const transformedBins = state.rackEditor.elements?.map((bin, index) => {
        const width = round(pixelsToUnits(Math.abs(bin.x2 - bin.x1), formData.unitType), 2);
        const height = round(pixelsToUnits(Math.abs(bin.y2 - bin.y1), formData.unitType), 2);
        return {
          // ...bin,
          rackId: shelf.id,
          id: isNaN(+bin.id) ? null : bin.id,
          name: bin.name,
          code: bin.code,
          labels: bin.labels,
          width: width,
          height: height,
          depth: bin.depth || 0,
          positionX: round(pixelsToUnits(bin.x1, formData.unitType), 2),
          positionY: round(pixelsToUnits(bin.y1, formData.unitType), 2),
          maxCapacity: bin.maxCapacity || null,
          levelId: null,
          columnId: null,
          createdAt: bin.createdAt,
          updatedAt: bin.updatedAt,
        };
      });

      const len = rackWidth > rackHeight ? formData.rackHeight : formData.rackWidth;
      const wid = rackWidth > rackHeight ? formData.rackWidth : formData.rackHeight;

      payload = {
        id: isNaN(+id) ? null : id,
        name: formData.rackName,
        code: formData.rackCode,
        warehouseLayoutId: location?.state?.warehouseLayoutId,
        positionX: round(formData.rackX, 2),
        //positionX: round(unitsToPixels(formData.rackX, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        positionY: round(formData.rackY, 2),
        //positionY: round(unitsToPixels(formData.rackY, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        length: round(len, 2),
        //length: round(unitsToPixels(len, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        width: round(wid, 2),
        //width: round(unitsToPixels(wid, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        orientation: shelf?.rotation || 0, //TODO: ROUND OFF TO 2 DECIMAL PLACES
        facingDirection: rackDirection.label,
        color,
        hasElementChanges,
        zIndex,
        objectType,
        bins: transformedBins,
        // columns,
        unitType: formData.unitType,
        maxCapacity: formData.maxCapacity,
      };
    } else if (objectType === "wall" || objectType === "area" || objectType === "dock" || objectType === "zone") {
      payload = {
        code: formData.rackCode,
        color,
        id: isNaN(+id) ? null : id,
        length: round(formData.rackHeight, 2),
        //length: round(unitsToPixels(formData.rackHeight, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        width: round(formData.rackWidth, 2),
        //width: round(unitsToPixels(formData.rackWidth, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        name: formData.rackName,
        positionX: round(formData.rackX, 2),
        //positionX: round(unitsToPixels(formData.rackX, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        positionY: round(formData.rackY, 2),
        //positionY: round(unitsToPixels(formData.rackY, formData.unitType), 2), //TODO: ROUND OFF TO 2 DECIMAL PLACES
        warehouseLayoutId: layoutId,
        facingDirection: rackDirection.label,
        zIndex,
        objectType,
        unitType: formData.unitType,
        orientation: rotation || 0,
      };
      // add wall height if object is wall
      if (objectType === "wall") {
        payload.height = formData.wallHeight ? round(formData.wallHeight, 2) : MIN_WALL_HEIGHT; // default to MIN_WALL_HEIGHT if not provided
      }
      if (objectType === "area") {
        //find area after, add subAreas
        const _area = state?.elements?.find((_element) => _element?.id === shelf?.id);
        const subAreas =
          _area?.subAreas?.map((subArea, index) => {
            return {
              areaId: shelf?.id,
              id: isNaN(+subArea.id) ? null : subArea.id,
              name: subArea?.name,
              code: subArea?.code,
              x: round(pixelsToUnits(subArea?.positionX, formData.unitType), 2),
              y: round(pixelsToUnits(subArea?.positionY, formData.unitType), 2),
              width: round(pixelsToUnits(subArea?.width, formData.unitType), 2),
              height: round(pixelsToUnits(subArea?.height, formData.unitType), 2),
              unitType: formData.unitType,
              maxCapacity: shelf?.maxCapacity === formData.maxCapacity ? subArea?.maxCapacity : formData.maxCapacity,
            };
          }) || [];
        payload.locations = { prevCode: shelf.code, newLocation: formData.rackCode };
        payload.subAreas = subAreas;
      }
    }

    if (shelf?.id && !shelf?.id.toString().startsWith("temp_")) {
      payload.id = shelf?.id;
    }

    // If object type is rack or area, add maxCapacity to the payload
    // if (objectType === "rack" || objectType === "area") {
    //   let maxCap = formData.maxCapacity || null;

    //   const deriveMaxCapacity = (subElements) => {
    //     const firstCap = subElements[0]?.maxCapacity;
    //     const allEqual = subElements.every((sub) => sub?.maxCapacity === firstCap);
    //     return allEqual ? firstCap : null;
    //   };

    //   // If no maxCapacity provided, try to derive from sub-elements
    //   if (!maxCap) {
    //     const subElements = objectType === "rack" ? payload?.bins : payload?.subAreas;

    //     if (subElements?.length) {
    //       maxCap = deriveMaxCapacity(subElements);
    //     }
    //   }

    //   // if(shelf?.maxCapacity === formData.maxCapacity) {
    //   //    const subElements = objectType === "rack" ? payload?.bins : payload?.subAreas;

    //   //   if (subElements?.length) {
    //   //     maxCap = deriveMaxCapacity(subElements);
    //   //   }

    //   // } else {
    //   //   maxCap = formData.maxCapacity;
    //   // }
    //   console.log(maxCap, "maxCap");

    //   payload = {
    //     ...payload,
    //     maxCapacity: maxCap ?? null,
    //   };
    //   console.log(payload, "payload");

    // }

    try {
      const {
        data: { data },
      } = await axios.post("layout-objects", payload);
      const message = messages(payload?.id);
      toast.success(message);

      //Get element index
      const elementIndex = state?.elements?.findIndex((element) => element?.id === shelf?.id);

      const transformedElement = transformElement(data);

      const elementReplacePaylod = { elementIndex, data: transformedElement };

      dispatch({ type: "REPLACE_ELEMENT", payload: elementReplacePaylod });
      // this updates the Data in the config panel
      dispatch({ type: "SET_SELECTED_ELEMENT", payload: transformedElement });
      handleShelfToggeler();
    } catch (error) {
      const {
        response: {
          data: { errorType, message, details },
        },
      } = error;
      const defaultErrorMessage = "Oops Something went wrong";
      if (errorType === "maxCapacityError") {
        setErrorPopup({ isOpen: true, response: { message, details } });
      } else {
        toast.error(message || defaultErrorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSave = (e) => {
    if (subAreas && shelf?.objectType === "area") {
      const updatedShelf = { ...shelf, code: formData.rackCode, name: formData.rackName };
      dispatch({
        type: "BATCH_DISPATCH",
        payload: [
          { type: "UPDATE_ELEMENT", payload: { ...updatedShelf, hasElementChanges: true, subAreas } },
          { type: "RE_SET_ELEMENTS", payload: subAreas },
        ],
      });
    }
    setSaveEvent(e);
    setSaveRequested(true);
  };

  useEffect(() => {
    if (saveRequested) {
      const { elements: childElements } = state.rackEditor;

      if (childElements?.length > 0) {
        const elementCodes = childElements.map((element) => element?.code);

        const duplicateCodes = getDuplicateCodes(elementCodes);
        if (duplicateCodes?.length > 0) {
          const [firstDuplicate] = duplicateCodes;
          toast.error(
            <>
              Code <b>{firstDuplicate}</b> is duplicated
            </>,
          );
          setSaveRequested(false);
          return;
        }
      }

      handleSaveRack(saveEvent);
      setSaveRequested(false);
    }
  }, [saveRequested]);

  const onToggleGenerateSubAreas = () => {
    setDialogData({
      title: "Generate Sub Areas",
      component_name: "SUB_AREAS",
      isModalActive: true,
      dilogWidth: 400,
      showCancelLabel: true,
      showConfirmLabel: true,
      cancelLabel: "Cancel",
      confirmLabel: "Generate",
      action: () => onGenerateSubAreas(),
    });
  };

  useEffect(() => {
    // set bins or sub areas based on object type
    if (shelf?.objectType === "rack") {
      dispatch({ type: "RE_SET_ELEMENTS", payload: shelf?.bins || [] });
    } else if (shelf?.objectType === "area") {
      dispatch({ type: "RE_SET_ELEMENTS", payload: shelf?.subAreas || [] });
    }
  }, []);

  const handleMaxCapacityInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^\d+$/.test(value) && value.length <= 4)) {
      setFormData({ ...formData, maxCapacity: value === "" ? null : Number(value) });
    }
  };

  const handleSubAreaUpdate = (updated) => {
    if (!updated) return;

    setSubAreas((prev) =>
      prev.map((subArea) => {
        const isMatch = updated.id
          ? subArea.id === updated.id // update by id if present
          : subArea.name === updated.name; // otherwise update by name

        return isMatch ? updated : subArea;
      }),
    );
  };
  const comment = true;

  return (
    <div className="configure-shelf" ref={parentRef} style={{ overflow: "scroll" }}>
      <div
        className="configure-shelf-header"
        style={{
          display: "flex",
          position: "sticky",
          top: 0,
          padding: "1rem 0rem",
          marginBottom: "1rem",
          alignItems: "center",
          backgroundColor: "white",
        }}
      >
        <h2 style={{ color: "black" }}>
          Configure {shelf?.name || shelf?.objectType || "Object"}
          {/* {labelGenerator()} */}
        </h2>

        {/* Unit labels */}
        <Suspense
          fallback={
            <div>
              <Spin size="small" />
            </div>
          }
        >
          <SegmentedToggle
            options={[
              { label: "Inch", value: UNIT_TYPES.inch },
              { label: "Feet", value: UNIT_TYPES.feet },
            ]}
            value={formData.unitType}
            onChange={setUnitType}
            style={{ marginLeft: "2rem" }}
          />
        </Suspense>

        <div className="configure-shelf-actions" style={{ marginLeft: "auto" }}>
          <Button type="default" className="cancel-button" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            style={{
              backgroundColor: "#607ad6",
              color: "#ffffff",
              transition: "background-color 0.3s ease",
              fontFamily: "Inter",
              ":hover": { backgroundColor: "#4a62a8" },
            }}
            type="default"
            loading={loading}
            onClick={onSave}
          >
            Save
          </Button>
        </div>
      </div>

      {!comment && (
        <div className="shelf-info">
          <div className="group-container">
            <div className="info-group">
              <label htmlFor="rackName">{labelGenerator("Name")} </label>
              <input type="text" id="rackName" name="rackName" onChange={handeChange} value={formData?.rackName} maxLength={25} />
            </div>

            <div className="info-group">
              <label htmlFor="rackCode">{labelGenerator("Code")}</label>
              <input type="text" id="rackCode" name="rackCode" onChange={handeChange} value={formData?.rackCode} />
            </div>
          </div>

          <div className="group-container">
            <div className="info-group">
              <label htmlFor="rackX">{labelGenerator("X")}</label>
              <input type="text" id="rackX" name="rackX" onChange={handeChange} value={formData?.rackX} />
            </div>

            <div className="info-group">
              <label htmlFor="rackY">{labelGenerator("Y")}</label>
              <input type="text" id="rackY" name="rackY" onChange={handeChange} value={formData?.rackY} />
            </div>
          </div>

          <div className="group-container">
            <div className="info-group">
              <label htmlFor="rackWidth">{labelGenerator("Width")}</label>
              <input type="text" id="rackWidth" name="rackWidth" onChange={handeChange} value={formData?.rackWidth} />
            </div>
            {/* If the object type is wall, change the label the height label to Length */}
            <div className="info-group">
              <label htmlFor="rackHeight"> {formData?.objectType === "wall" ? labelGenerator("Length") : labelGenerator("Height")}</label>
              <input type="text" id="rackHeight" name="rackHeight" onChange={handeChange} value={formData?.rackHeight} />
            </div>
          </div>
          {/* Only show for walls */}
          {shelf?.objectType === "wall" && (
            <div className="group-container">
              <div className="info-group">
                <label htmlFor="wallHeight">{labelGenerator("Height")}</label>
                <input
                  type="text"
                  id="wallHeight"
                  name="wallHeight"
                  onChange={handeChange}
                  value={formData?.wallHeight || ""}
                  placeholder="Enter wall height"
                />
              </div>
            </div>
          )}
          {/* Max Capacity Dropdown */}
          {labelGenerator()?.toLowerCase()?.trim() === "rack" || labelGenerator()?.toLowerCase()?.trim() === "area" ? (
            <div className="group-container">
              <div className="info-group">
                <label htmlFor="maxCapacity" className="max-capacity-label">
                Max Capacity
                <span className="info-icon">
                  {/* Antd info circled icon */}
                  <ToolTip placement="top" title={`When unset or 0, capacity is unlimited for this ${labelGenerator()}.`}>
                    <InfoCircleFilled />
                  </ToolTip>
                </span>
              </label>
              <input
                type="text"
                id="maxCapacity"
                name="maxCapacity"
                value={formData?.maxCapacity ?? ""}
                onChange={handleMaxCapacityInputChange}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                placeholder="Enter Capacity"
                maxLength={4}
              />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Light color divider */}
      {/* <div className="divider" style={{ margin: "0.5rem 0", height: "1px", backgroundColor: "#e0e0e0b2" }}></div> */}

      {/* Rack Front View */}

      <div className="level-configure-wrapper">
        {shelf?.objectType === "rack" && <RackFrontView formData={formData} />}

        {shelf?.objectType === "area" && (
          <div className="sub-areas-view">
            <heading className="sub-areas-view-header">
              <h3>Sub Areas</h3>{" "}
              <div>
                <button className="split-area-button" type="button" onClick={onToggleGenerateSubAreas}>
                  Split Area
                </button>
              </div>
            </heading>
            <div className="sub-areas-board">
              {subAreas && subAreas.length === 0 ? (
                <div className="sub-areas-empty">No sub areas data.</div>
              ) : (
                <SubAreaBoard subAreas={subAreas || shelf?.subAreas} onSubAreaUpdate={handleSubAreaUpdate} />
              )}
            </div>
          </div>
        )}
      </div>

      {dialogData?.isModalActive && (
        <AppModal
          title={dialogData?.title}
          width={dialogData?.dilogWidth}
          isModalActive={dialogData?.isModalActive}
          onHandleModalClose={onCloseDialogHandler}
          onHandleModalSubmit={onConfirmDialogHandler}
          showCancelLabel={dialogData?.showCancelLabel}
          showConfirmLabel={dialogData?.showConfirmLabel}
          cancelLabel={dialogData?.cancelLabel}
          confirmLabel={dialogData?.confirmLabel}
        >
          {componentToView(dialogData?.component_name)}
        </AppModal>
      )}

      {errorPopup.isOpen && (
        <AppModal
          title="Max Capacity Error"
          width="650px"
          isModalActive={errorPopup.isOpen}
          onHandleModalClose={() => setErrorPopup({ isOpen: false, message: "" })}
          showCancelLabel={true}
          showConfirmLabel={false}
          cancelLabel="Close"
        >
          <BinCapacityError response={errorPopup.response} />
        </AppModal>
      )}
    </div>
  );
};

export default ConfigureShelf;
